import { useLazyQuery, useMutation } from '@apollo/client';
import { STRIPE_CREATE_LOGIN_LINK, STRIPE_RETRIEVE_CONNECTED_ACCOUNT } from '../../pages/Settings/graphql/queries';
import { STRIPE_CONNECT_CONNECTED_ACCOUNT } from '../../pages/Settings/graphql/mutations';
import { GraphQLFetchPolicies } from '../enums';
import { StripeAccount, StripeCreateAccountOutput } from 'shared/types/graphql';
import { useEffect, useMemo, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';

export const useStripe = (): {
  account: StripeAccount | undefined;
  onboardingComplete: boolean | undefined;
  loading: boolean;
  login: () => void;
} => {
  const { currentClinicId } = useClinicUser();

  const [onboardingComplete, setOnboardingComplete] = useState<boolean>();

  // Create Stripe Account
  const [createAccount, { loading: creatingAccount }] = useMutation<{
    stripeCreateConnectedAccount: StripeCreateAccountOutput;
  }>(STRIPE_CONNECT_CONNECTED_ACCOUNT);

  // Get Stripe Account
  const [getAccountData, { data: accountData, loading: loadingAccount }] = useLazyQuery<{
    stripeRetrieveConnectedAccount: StripeCreateAccountOutput;
  }>(STRIPE_RETRIEVE_CONNECTED_ACCOUNT, { fetchPolicy: GraphQLFetchPolicies.NetworkOnly });

  // Get Stripe Login Link & Open link in new window
  const [getLoginLink, { loading: loadingLoginLink, refetch: refetchLoginLink }] = useLazyQuery(
    STRIPE_CREATE_LOGIN_LINK,
    { onCompleted: (data) => window.open(data.stripeCreateLoginLink.url) },
  );

  // Set Account Data
  const account = useMemo((): StripeAccount | undefined => {
    return accountData?.stripeRetrieveConnectedAccount?.account;
  }, [accountData]);

  // Flag for tracking Clinic Select
  const [clinicId, setClinicId] = useState<string | undefined>(undefined);

  // Effect Hook handles clinic switching & updates to the Stripe account
  useEffect(() => {
    if (clinicId !== currentClinicId && !loadingAccount && !accountData) {
      getAccountData();
      // Setting clinicId stops Effect Loop
      setClinicId(currentClinicId);
    }
    if (!!account && !loadingAccount) {
      setOnboardingComplete(account.chargesEnabled && account.payoutsEnabled && !!account.defaultAccount);
    }
  }, [loadingAccount, accountData, clinicId, currentClinicId, account, getAccountData, setClinicId]);

  const login = (): void => {
    // Stop multiple login calls
    if (loadingLoginLink) return;

    // Calling login without an account initiates Stripe Onboarding
    if (!account) {
      onboardWithStripe();
      return;
    }
    // Do Login
    !refetchLoginLink ? getLoginLink() : refetchLoginLink();
  };

  const onboardWithStripe = (): void => {
    const options = { variables: { data: { redirectUrl: window.location.href } } };
    createAccount(options).then(({ data }) => {
      const onboardingUrl = data?.stripeCreateConnectedAccount?.onboarding?.url;
      if (onboardingUrl) {
        // Stripe Onboarding Redirects to URL defined above (target is current window/tab)
        window.location.href = onboardingUrl;
      }
    });
  };

  return {
    account,
    onboardingComplete,
    login,
    loading: loadingAccount || loadingLoginLink || creatingAccount,
  };
};
